"use client"

import { useState, useEffect, useMemo } from "react"
import { Card, CardContent } from "../../ui/card"
import { Button } from "../../ui/button"
import { apiClient } from "../../lib/apiClient"
import { useToast } from "../../hooks/use-toast"
import { Loader2, Edit2, Plus, Network } from "lucide-react"
import { AddEditRoleMenuMappingModal } from "../../modals/add-edit-role-menu-mapping-modal"

interface Menu {
  menuID: number
  menuName: string
}

interface RoleMenuMapping {
  roleID: number
  roleName: string
  menus: Menu[]
}

export function MenuRoleMappingPage() {
  const [roleMappings, setRoleMappings] = useState<RoleMenuMapping[]>([])
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<"add" | "edit">("add")
  const [selectedRole, setSelectedRole] = useState<RoleMenuMapping | null>(null)
  const { toast } = useToast()

  // Extract all unique menus from role mappings
  const allMenus = useMemo(() => {
    const menuMap = new Map<number, Menu>()
    roleMappings.forEach((role) => {
      role.menus?.forEach((menu) => {
        if (!menuMap.has(menu.menuID)) {
          menuMap.set(menu.menuID, menu)
        }
      })
    })
    return Array.from(menuMap.values()).sort((a, b) => a.menuName.localeCompare(b.menuName))
  }, [roleMappings])

  // Fetch role-menu mappings
  const fetchRoleMappings = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get("/Admin/get-role-menu-mapping")
      const data = Array.isArray(response.data) ? response.data : []
      setRoleMappings(data)
    } catch (error: any) {
      console.error("Error fetching role-menu mappings:", error)
      toast({
        title: "Error",
        description: error?.response?.data?.message || error?.message || "Failed to load role-menu mappings",
        duration: 4000,
        className: "border border-red-300",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRoleMappings()
  }, [])

  // Handle edit
  const handleEdit = (role: RoleMenuMapping) => {
    setSelectedRole(role)
    setModalMode("edit")
    setIsModalOpen(true)
  }

  // Handle add new role
  const handleAddNewRole = () => {
    setSelectedRole(null)
    setModalMode("add")
    setIsModalOpen(true)
  }

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedRole(null)
  }

  // Handle save success - refresh data
  const handleSaveSuccess = () => {
    fetchRoleMappings()
  }


  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
            <p className="text-gray-500">Loading role-menu mappings...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Network className="h-5 w-5 text-blue-600" />
              <h1 className="text-xl font-semibold text-gray-900">Menu Role Mapping</h1>
            </div>
            <Button
              onClick={handleAddNewRole}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
              size="sm"
            >
              <Plus className="h-4 w-4" />
              Add New Role
            </Button>
          </div>

          {/* Role Cards */}
          {roleMappings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Network className="h-10 w-10 mx-auto mb-3 text-gray-400" />
              <p>No role-menu mappings found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roleMappings.map((role) => {
                const menuCount = role.menus?.length || 0

                return (
                  <Card
                    key={role.roleID}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-3 flex flex-col">
                      {/* Role Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-900">{role.roleName}</h2>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {menuCount} {menuCount === 1 ? "menu" : "menus"}
                          </span>
                        </div>

                        {/* Action Icons */}
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(role)}
                            className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>

                      {/* Menu Items List */}
                      {menuCount > 0 ? (
                        <div className="overflow-y-auto">
                          <h3 className="text-xs font-semibold text-gray-700 mb-1">Associated Menu Items:</h3>
                          <div className="flex flex-wrap gap-1.5">
                            {role.menus.map((menu) => (
                              <span
                                key={menu.menuID}
                                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium"
                              >
                                {menu.menuName}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-500 text-xs">
                          No menus assigned
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <AddEditRoleMenuMappingModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSaveSuccess}
        initialData={selectedRole}
        mode={modalMode}
        allMenus={allMenus}
      />
    </div>
  )
}

